import { useState } from "react";
import { Plus, Pencil, Trash2, HelpCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { AGENT_SETTINGS } from "@/constants/testIds";
import FaqModal from "@/components/agent/FaqModal";

/** Preguntas frecuentes de la knowledge base del agente. */
export default function FaqsTab({ faqs, loading, onCreate, onUpdate, onDelete }) {
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  if (loading) return <p className="text-sm text-muted-foreground">Cargando...</p>;

  const handleSubmit = async (form, faq) => {
    if (faq) await onUpdate(faq.id, form);
    else await onCreate(form);
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    await onDelete(deleteTarget.id);
    setDeleteTarget(null);
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Button className="bg-brand hover:bg-brand-hover text-white gap-2" onClick={() => { setEditing(null); setModalOpen(true); }} data-testid={AGENT_SETTINGS.faqAddButton}>
          <Plus className="w-4 h-4" /> Nueva pregunta
        </Button>
      </div>

      {faqs.length === 0 ? (
        <div className="bg-white border border-border rounded-xl p-8 text-center" data-testid={AGENT_SETTINGS.faqEmptyState}>
          <HelpCircle className="w-8 h-8 text-muted-foreground/40 mx-auto mb-2" />
          <p className="text-sm text-muted-foreground">Todavia no hay preguntas frecuentes cargadas.</p>
        </div>
      ) : (
        <div className="space-y-2" data-testid={AGENT_SETTINGS.faqList}>
          {faqs.map((f) => (
            <div key={f.id} className="bg-white border border-border rounded-xl p-4 flex items-start justify-between gap-3" data-testid={`${AGENT_SETTINGS.faqRowPrefix}${f.id}`}>
              <div>
                <p className="text-sm font-medium text-foreground">{f.question}</p>
                <p className="text-sm text-muted-foreground mt-1">{f.answer}</p>
              </div>
              <div className="flex gap-1 shrink-0">
                <Button variant="ghost" size="icon" onClick={() => { setEditing(f); setModalOpen(true); }} data-testid={`${AGENT_SETTINGS.faqEditButtonPrefix}${f.id}`}>
                  <Pencil className="w-4 h-4" />
                </Button>
                <Button variant="ghost" size="icon" onClick={() => setDeleteTarget(f)} data-testid={`${AGENT_SETTINGS.faqDeleteButtonPrefix}${f.id}`}>
                  <Trash2 className="w-4 h-4 text-destructive" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <FaqModal open={modalOpen} onOpenChange={setModalOpen} faq={editing} onSubmit={handleSubmit} />

      <AlertDialog open={Boolean(deleteTarget)} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminar esta pregunta frecuente?</AlertDialogTitle>
            <AlertDialogDescription>Esta accion no se puede deshacer.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive hover:bg-destructive/90">Eliminar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
